import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartProvider } from "@/contexts/CartContext";
import { FlyToCartProvider } from "@/hooks/useFlyToCart";
import { lazy, Suspense } from "react";
import { MAINTAINANCE_MODE } from "./lib/constants.ts";
import Maintenance from "./components/Maintainance.tsx";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop.tsx";
import AdminProtectedRoute from "./components/AdminProtectedRoute.tsx";

import Index from "./pages/Index.tsx";
import BlogPost8Page from "./pages/BlogPost8Page.tsx";
import BlogPost9Page from "./pages/BlogPost9Page.tsx";
import BlogPost10Page from "./pages/BlogPost10Page.tsx";
import BlogPost11Page from "./pages/BlogPost11Page.tsx";
import BlogPost12Page from "./pages/BlogPost12Page.tsx";
import BlogPost13Page from "./pages/BlogPost13Page.tsx";
import BlogPost14Page from "./pages/BlogPost14Page.tsx";
import BlogPost15Page from "./pages/BlogPost15Page.tsx";
const BrandPage = lazy(() => import("./pages/BrandPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogPost1Page = lazy(() => import("./pages/BlogPost1Page.tsx"));
const BlogPost2Page = lazy(() => import("./pages/BlogPost2Page.tsx"));
const BlogPost3Page = lazy(() => import("./pages/BlogPost3Page.tsx"));
const BlogPost4Page = lazy(() => import("./pages/BlogPost4Page.tsx"));
const BlogPost5Page = lazy(() => import("./pages/BlogPost5Page.tsx"));
const BlogPost6Page = lazy(() => import("./pages/BlogPost6Page.tsx"));
const BlogPost7Page = lazy(() => import("./pages/BlogPost7Page.tsx"));
const BlogDraftPage = lazy(() => import("./pages/BlogDraftPage.tsx"));
const PolicyPage = lazy(() => import("./pages/PolicyPage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const ProductBuilderPage = lazy(() => import("./pages/ProductBuilderPage.tsx"));
const ProductListingPage = lazy(() => import("./pages/ProductListingPage.tsx"));
const CartPage = lazy(() => import("./pages/CartPage.tsx"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage.tsx"));
const OrderHistoryPage = lazy(() => import("./pages/OrderHistoryPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminProduct = lazy(() => import("./pages/admin/AdminProduct.tsx"));
const AdminOrder = lazy(() => import("./pages/admin/AdminOrder.tsx"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail.tsx"));


const queryClient = new QueryClient();

const App = () => {
  const isMaintenance = String(MAINTAINANCE_MODE).toLowerCase() === 'true';
  if (isMaintenance) {
    return <Maintenance />;
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <CartProvider>
            <FlyToCartProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/thuong-hieu" element={<BrandPage />} />
                    <Route path="/sharing-blog" element={<BlogPage />} />
                    <Route
                      path="/sharing-blog/da-khuech-tan-tinh-dau-la-gi"
                      element={<BlogPost1Page />}
                    />
                    <Route
                      path="/sharing-blog/cach-su-dung-da-khuech-tan-tinh-dau"
                      element={<BlogPost2Page />}
                    />
                    <Route
                      path="/sharing-blog/tinh-dau-thom-phong-khuech-tan"
                      element={<BlogPost3Page />}
                    />
                    <Route
                      path="/sharing-blog/so-sanh-da-khuech-tan-que-may-may-xong"
                      element={<BlogPost4Page />}
                    />
                    <Route
                      path="/sharing-blog/cac-loai-da-khuech-tan-tinh-dau"
                      element={<BlogPost5Page />}
                    />
                    <Route
                      path="/sharing-blog/cach-bao-quan-tinh-dau-thien-nhien"
                      element={<BlogPost6Page />}
                    />
                    <Route
                      path="/sharing-blog/da-khuech-tan-tinh-dau-qua-tang-tinh-te"
                      element={<BlogPost7Page />}
                    />
                    <Route
                      path="/sharing-blog/su-that-ve-tinh-an-toan-cua-tinh-dau-khuech-tan"
                      element={<BlogPost8Page />}
                    />
                    <Route
                      path="/sharing-blog/bi-quyet-chon-tinh-dau-phu-hop-voi-da-khuech-tan"
                      element={<BlogPost9Page />}
                    />
                    <Route
                      path="/sharing-blog/da-khuech-tan-tinh-dau-qua-tang-tot-nghiep"
                      element={<BlogPost10Page />}
                    />
                    <Route
                      path="/sharing-blog/cach-chon-tinh-dau-phu-hop-khong-gian-tam-trang"
                      element={<BlogPost11Page />}
                    />
                    <Route
                      path="/sharing-blog/mua-da-khuech-tan-tinh-dau-chinh-hang-tphcm"
                      element={<BlogPost12Page />}
                     />
                     <Route
                      path="/sharing-blog/cau-chuyen-thuong-hieu-memys"
                      element={<BlogPost13Page />}
                    />
                    <Route
                      path="/sharing-blog/xu-huong-qua-tang-ca-nhan-hoa-gen-z"
                      element={<BlogDraftPage />}
                    />
                    <Route 
                      path="/sharing-blog/khu-mui-o-to-bang-da-khuech-tan-tinh-dau"
                      element={<BlogPost14Page />}
                    />
                    <Route 
                      path="/sharing-blog/huong-dan-dat-mua-da-khuech-tan-tinh-dau-memys"
                      element={<BlogPost15Page />}
                    />
                    <Route path="/chinh-sach" element={<PolicyPage />} />
                    <Route path="/lien-he" element={<ContactPage />} />
                    <Route path="/san-pham/:categorySlug?" element={<ProductListingPage />} />
                    <Route path="/san-pham/customize" element={<ProductBuilderPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/thanh-toan" element={<CheckoutPage />} />
                    <Route path="/lich-su-mua-hang" element={<OrderHistoryPage />} />
                    <Route path="*" element={<NotFound />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route element={<AdminProtectedRoute />}>
                      <Route path="/admin/product" element={<AdminProduct />} />
                      <Route path="/admin/order" element={<AdminOrder />} />
                      <Route path="/admin/email" element={<AdminEmail />} />
                    </Route>
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </FlyToCartProvider>
          </CartProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;